import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Avatar,
  Divider,
  Tooltip
} from "@heroui/react";
import { userProfileUpdateApi } from "../../../api/methods-marketplace";

// Validation schema
const validationSchema = yup.object({
  first_name: yup
    .string()
    .trim()
    .strict(true)
    .matches(/^[^\s]+(?:\s[^\s]+)*$/, "Invalid name format")
    .required("First name is required"),
  last_name: yup
    .string()
    .trim()
    .strict(true)
    .matches(/^[^\s]+(?:\s[^\s]+)*$/, "Invalid name format")
    .required("Last name is required")
});

const ProfileEditModal = ({ isOpen, onOpenChange, user, onClose, onSave }) => {
  const [previewImages, setPreviewImages] = useState({
    profile: null,
    banner: null
  });
  const [hasInteracted, setHasInteracted] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, errors, isDirty }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      profile_image: null,
      banner_image: null
    }
  });

  const isDisabled = isSubmitting || !isDirty;
  const showTooltip = hasInteracted && !isDirty;

  // Reset form when user data changes or modal opens
  useEffect(() => {
    if (user && isOpen) {
      reset({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        profile_image: user?.profile_image || null,
        banner_image: user?.banner_image || null
      });
      // Reset preview images
      setPreviewImages({
        profile: null,
        banner: null
      });
      setHasInteracted(false);
    }
  }, [user, isOpen, reset]);

  const handleImageChange = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      setHasInteracted(true);
      const fieldName = type === "profile" ? "profile_image" : "banner_image";
      setValue(fieldName, file, { shouldDirty: true });

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImages((prev) => ({
          ...prev,
          [type]: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data) => {
    if (!isDirty) {
      setHasInteracted(true);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("user[first_name]", data.first_name);
      formData.append("user[last_name]", data.last_name);

      // Append files (if they exist and are valid)
      if (data.profile_image instanceof File) {
        formData.append("user[attachment]", data.profile_image);
      }
      if (data.banner_image instanceof File) {
        formData.append("user[banner]", data.banner_image);
      }

      const response = await userProfileUpdateApi(formData);
      console.log("🚀 ~ onSubmit ~ response:", response);

      if (response) {
        onSave(response?.data);
      }
    } catch (error) {
      console.error("Failed to save profile:", error);
    }
  };

  const handleClose = () => {
    // Reset form and preview images
    reset();
    setPreviewImages({
      profile: null,
      banner: null
    });
    setHasInteracted(false);
    onClose();
  };

  const removeImage = (type) => {
    setHasInteracted(true);
    const fieldName = type === "profile" ? "profile_image" : "banner_image";
    setValue(fieldName, null, { shouldDirty: true });
    setPreviewImages((prev) => ({
      ...prev,
      [type]: null
    }));
  };

  const handleFieldChange = (onChange) => (e) => {
    setHasInteracted(true);
    onChange(e);
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="2xl"
      scrollBehavior="inside"
      isDismissable={!isSubmitting}
      isKeyboardDismissDisabled={isSubmitting}
      classNames={{
        closeButton: "hidden"
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h2 className="text-2xl font-bold text-gray-900">Edit Profile</h2>
              <p className="text-sm text-gray-600">
                Update your profile information
              </p>
            </ModalHeader>

            <ModalBody className="gap-6 px-0">
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-6"
              >
                <div className="flex flex-col gap-6 px-4">
                  <div className="flex flex-col gap-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Banner Image
                    </h3>
                    <div className="relative">
                      <div
                        className="w-full h-32 bg-gradient-to-r from-[#343008] to-black/60 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() =>
                          document.getElementById("banner-image-input").click()
                        }
                        style={{
                          backgroundImage: previewImages.banner
                            ? `url("${previewImages.banner}")`
                            : user?.banner_image
                            ? `url("${user.banner_image}")`
                            : undefined,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat"
                        }}
                      >
                        {!previewImages.banner && !user?.banner_image && (
                          <div className="flex items-center justify-center h-full">
                            <div className="text-center text-white font-medium">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-8 h-8 mx-auto mb-2"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Z"
                                />
                              </svg>
                              <p className="text-sm">Click to upload banner</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Button
                          isIconOnly
                          size="sm"
                          className="bg-black/70 text-white hover:bg-black/90"
                          onPress={() =>
                            document
                              .getElementById("banner-image-input")
                              .click()
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-4 h-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z"
                            />
                          </svg>
                        </Button>
                        {previewImages.banner && (
                          <Button
                            isIconOnly
                            size="sm"
                            className="bg-red-500/70 text-white hover:bg-red-600/90"
                            onPress={() => removeImage("banner")}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-4 h-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </Button>
                        )}
                      </div>
                      <input
                        id="banner-image-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageChange(e, "banner")}
                      />
                    </div>
                  </div>

                  <Divider />

                  <div className="flex flex-col items-center gap-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Profile Picture
                    </h3>
                    <div className="relative mb-3">
                      <Avatar
                        radius="md"
                        src={previewImages.profile || user?.profile_image}
                        className="w-24 h-24 text-large"
                        name={user?.first_name?.charAt(0)}
                      />
                      <div className="absolute -bottom-3 -right-1 flex gap-1">
                        <Button
                          isIconOnly
                          size="sm"
                          className="bg-black text-white hover:bg-gray-800"
                          onPress={() =>
                            document
                              .getElementById("profile-image-input")
                              .click()
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-4 h-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z"
                            />
                          </svg>
                        </Button>

                        {previewImages.profile && (
                          <Button
                            isIconOnly
                            size="sm"
                            className="bg-red-500 text-white hover:bg-red-600"
                            onPress={() => removeImage("profile")}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-3 h-3"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </Button>
                        )}
                      </div>
                      <input
                        id="profile-image-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageChange(e, "profile")}
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      Click the camera icon to change your profile picture
                    </p>
                  </div>

                  <Divider />

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Basic Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Controller
                        name="first_name"
                        control={control}
                        render={({ field: { onChange, ...field } }) => (
                          <Input
                            {...field}
                            onChange={handleFieldChange(onChange)}
                            label="First Name"
                            placeholder="Enter your first name"
                            variant="bordered"
                            isInvalid={!!errors.first_name}
                            errorMessage={errors.first_name?.message}
                            classNames={{
                              input: "text-black",
                              label: "text-gray-700 font-medium"
                            }}
                            maxLength={25}
                            onKeyDown={(e) => {
                              if (e.key === " ") e.preventDefault();
                            }}
                          />
                        )}
                      />
                      <Controller
                        name="last_name"
                        control={control}
                        render={({ field: { onChange, ...field } }) => (
                          <Input
                            {...field}
                            onChange={handleFieldChange(onChange)}
                            label="Last Name"
                            placeholder="Enter your last name"
                            variant="bordered"
                            isInvalid={!!errors.last_name}
                            errorMessage={errors.last_name?.message}
                            classNames={{
                              input: "text-black",
                              label: "text-gray-700 font-medium"
                            }}
                            maxLength={22}
                            onKeyDown={(e) => {
                              if (e.key === " ") e.preventDefault();
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
                <ModalFooter className="gap-3 px-4">
                  <Button
                    color="danger"
                    variant="solid"
                    onPress={handleClose}
                    className="font-medium"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Tooltip
                    content="No changes to save"
                    isOpen={showTooltip}
                    placement="top"
                    color="default"
                    showArrow={true}
                  >
                    <Button
                      type="submit"
                      variant="solid"
                      className={`${
                        isDisabled
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600"
                      } text-black font-semibold transition-all duration-200 ease-in-out rounded-full px-6 py-2 shadow-sm hover:shadow-md`}
                      isLoading={isSubmitting}
                      disabled={isDisabled}
                    >
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </Button>
                  </Tooltip>
                </ModalFooter>
              </form>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ProfileEditModal;
